import {
	IAdvancedQuickRepliesItem,
	IChatMessagesOut,
	IChatTextMessage,
	MessageType,
} from '@enuan/chat__utils';
import classNames from 'classnames';
import { useLogError } from '../../ErrorBoundary/useLogError';
import { Button, IButtonProps } from '../../UI';
import { useSendMessage } from '../../../hooks';
import React, { useCallback, useMemo } from 'react';
import { useMessageContext } from '../message.context';
import { Stack, styled } from '@mui/material';

export type IQuickRepliesWrapperProps = {
	onClick?: (option: IAdvancedQuickRepliesItem) => void;
};

export const QuickRepliesWrapper = (props: IQuickRepliesWrapperProps) => {
	const { onClick } = props;
	const { message } = useMessageContext<IChatTextMessage>();
	const { metadata } = message;

	const logError = useLogError('QuickReplies', { message });

	const sendMessage = useSendMessage();
	const sendQuickReply = useCallback(
		(text: string) => {
			const message: IChatMessagesOut<IChatTextMessage> = {
				_type: MessageType.TEXT,
				_data: text,
			};
			sendMessage(message).catch((error) =>
				logError('sendQuickReply', { error, payload: { message } }),
			);
		},
		[sendMessage, logError],
	);

	const rows = useMemo<IAdvancedQuickRepliesItem[][]>(() => {
		if (metadata?.['gw.advanced_quick_replies']) {
			return metadata['gw.advanced_quick_replies'].map((row) =>
				Array.isArray(row)
					? row.map((column) =>
							typeof column === 'string' ? { label: column } : column,
						)
					: typeof row === 'string'
						? [{ label: row }]
						: [row],
			);
		} else if (metadata?.['telegram.reply_markup']) {
			return metadata['telegram.reply_markup'].keyboard.map((row) =>
				row.map((item) => ({ label: item })),
			);
		} else if (metadata?.['gw.quick_replies']) {
			return [
				metadata?.['gw.quick_replies'].reduce<IAdvancedQuickRepliesItem[]>(
					(prev, curr) => [...prev, { label: curr }],
					[],
				),
			];
		}
		return [];
	}, [metadata]);

	const onOptionClick = useCallback(
		(option: IAdvancedQuickRepliesItem) => {
			const { label, message } = option;
			sendQuickReply(message ?? label);

			onClick?.(option);
		},
		[onClick, sendQuickReply],
	);

	return (
		<>
			{rows.map((opts, keyRow) => {
				return (
					<QuickReplies
						opts={opts}
						onOptionClick={onOptionClick}
						key={keyRow}
					/>
				);
			})}
		</>
	);
};

const QuickReplies = styled(
	({
		opts,
		onOptionClick,
		className,
	}: {
		opts: IAdvancedQuickRepliesItem[];
		onOptionClick: (option: IAdvancedQuickRepliesItem) => void;
		className?: string;
	}) => (
		<Stack
			direction="row"
			justifyContent="space-between"
			alignItems="stretch"
			flexWrap="wrap"
			className={classNames(className, 'message__quick-replies')}
			sx={{
				'--quick-replies-count': opts.length === 1 ? 2 : opts.length,
			}}
		>
			{opts.map((opt, keyButton) => {
				const { label, className, color } = opt;
				return (
					<Button
						className={classNames('quick-replies__option', className)}
						color={color as IButtonProps['color']}
						key={keyButton}
						onClick={() => onOptionClick(opt)}
					>
						{label}
					</Button>
				);
			})}
		</Stack>
	),

	{ target: 'QuickReplies-root', label: 'QuickReplies' },
)({
	marginTop: '0em',
	marginLeft: '-0.8em',
	marginRight: '-0.8em',
	'& .quick-replies__option': {
		padding: '0.6em 0.9em',
		margin: '0.175em 0.8em',
		whiteSpace: 'pre-wrap',

		fontFamily: "'Roboto', sans-serif",
		fontWeight: 400,
		fontSize: '1.3em',
		transition: 'all 150ms',

		borderTopLeftRadius: '0.6em',
		borderBottomLeftRadius: '0.6em',
		borderTopRightRadius: '0.6em',

		flex: '1 0 calc((100% / var(--quick-replies-count)) - (0.8em * var(--quick-replies-count)))',
		"&:not([class^='button--']):not([class*=' button--'])": {
			backgroundColor: 'var(--message__quick-replies__option_background)',
			color: 'var(--message__quick-replies__option_color)',
			'&:hover': {
				backgroundColor:
					'var(--message__quick-replies__option--hover_background)',
				color: 'var(--message__quick-replies__option--hover_color)',
			},
		},
	},
});
